/*
 * File: prop-innate.ts
 * File Created: 2024-01-12 08:42:40
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-16 12:17:55
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import Vue from "vue";
import { createDecorator } from "vue-class-component";
import { ObjectHelper, Retrier, StringHelper } from "../utilities";
import { IMultiple } from "@root/components/inputs/core/lib";

import debug from "@/config/default/debug.js";

/// `PropInnateSync` does these things:
/// 1) make innate value that works for both binding and without binding.
/// 2) return default() if value is null or undefined.
/// 3) return Array if `multiple` is true and vice versa.

let propInnateSyncCount = 0;
export function PropInnateSync(propName: string, options: any = {}) {
    return (target: Vue, key: string) => {
        createDecorator((componentOptions, k) => {
            // Initialize prop
            componentOptions.props = componentOptions.props || {};
            componentOptions.props[propName] = options;

            // Define a unique internal property name
            const internalPropName = `pvt${StringHelper.capitalize(k)}`;

            // Define data and watcher
            const originalData = componentOptions.data;
            componentOptions.data = function (this: Vue) {
                const data =
                    typeof originalData === "function"
                        ? (originalData as any).call(this)
                        : {};
                data[internalPropName] = this[propName]; // Initialize innateValue with prop value
                return data;
            };

            // Watcher for prop changes
            componentOptions.watch = componentOptions.watch || {};
            componentOptions.watch[propName] = {
                immediate: true,
                handler(this: Vue, newVal, oldVal) {
                    newVal = getMultipleValue(options, this["multiple"], newVal);

                    /// debugger /////////////////////
                    {
                        debug.debugFormAndTable &&
                            propInnateSyncCount++ &&
                            console.log(
                                `<PropInnateSync> compare '${propName}', new:"`,
                                newVal,
                                `", old:"`,
                                oldVal,
                                `", equal?`,
                                ObjectHelper.equals(newVal, oldVal),
                                ", count:",
                                propInnateSyncCount,
                            );
                    }
                    //////////////////////////////////

                    /// if equal, ignore update.
                    if (ObjectHelper.equals(newVal, oldVal)) return;
                    /// trigger update on change.
                    this[internalPropName] = newVal;
                    (this as any).$emit(`update:${propName}`, newVal);
                },
            };

            // Define a computed property for two-way binding
            componentOptions.computed = componentOptions.computed || {};
            componentOptions.computed[k] = {
                get() {
                    return getMultipleValue(
                        options,
                        this["multiple"],
                        this[internalPropName],
                    );
                },
                set(value) {
                    if (value !== this[internalPropName]) {
                        (this as any).$emit(`update:${propName}`, value); // Emit update event on programmatic changes
                        this[internalPropName] = value; // Update innateValue
                    }
                },
            };
        })(target, key);
    };
}

function getMultipleValue(options, multiple: IMultiple, value) {
    {
        /// if allow type is only "Array", don't handle `multiple`.
        const type = options.type;
        if (type === Array || (type?.length === 1 && type?.[0] === Array)) return value;
    }

    if (multiple === true || multiple === "range") {
        if (Array.isArray(value)) {
            return value
                .map((o) => {
                    return getGeneratedValue(options, o);
                })
                .filter((o) => o != null);
        } else {
            return [getGeneratedValue(options, value)].filter((o) => o != null);
        }
    } else {
        if (Array.isArray(value)) return getGeneratedValue(options, value[0]);
        else return getGeneratedValue(options, value);
    }
}

function getGeneratedValue(options, value) {
    /// make default value if null | undefined
    let result =
        value != null
            ? value
            : options.default === undefined
              ? value
              : typeof options.default === "function"
                ? options.default()
                : options.default;
    return result;
}
